import { MEDALS, type MedalListItem, type MedalTallyColumn, type MedalTallyRow } from "@seagames/contracts";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { errorMessage } from "../../lib/api/messages";
import { medalListQuery } from "../../lib/desk/queries";
import { formatDateTime, formatInteger, formatShort } from "../../lib/i18n/format";
import { cellOf } from "../../lib/medals/tally";
import { DayColumns } from "../charts/day-columns";
import { StackedBars } from "../charts/stacked-bar";
import { MEDAL_COLOR, MedalBadge } from "../medals/medal-chip";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";

/**
 * One NOC's medals — the tally's drawer: its standing and totals, its medals
 * per sport and per day (both from the tally calls the page already holds:
 * `breakdown=sport` and `breakdown=day`, no request of their own), and the
 * medals themselves — `GET /medals?responsible_organization_id=`, a keyset
 * page per "Show more", grouped by sport as they arrive.
 */
export function ContingentDrawer({
	gamesId,
	sportId,
	row,
	ofRows,
	shared,
	sportCells,
	sportColumns,
	dayCells,
	dayColumns,
}: {
	gamesId: string;
	sportId: string | undefined;
	row: MedalTallyRow;
	ofRows: number;
	shared: boolean;
	/** The NOC's row of the sport breakdown, and its columns (undefined while loading). */
	sportCells: MedalTallyRow | undefined;
	sportColumns: readonly MedalTallyColumn[];
	dayCells: MedalTallyRow | undefined;
	dayColumns: readonly MedalTallyColumn[];
}) {
	const { t } = useTranslation("tally");
	const tm = useTranslation("medals").t;
	const tc = useTranslation().t;
	const list = useInfiniteQuery(medalListQuery({ games_id: gamesId, sport_id: sportId, responsible_organization_id: row.noc.responsible_organization_id }));
	const medals = list.data?.pages.flatMap((p) => p.items) ?? [];
	const bySport = groupBySport(medals);
	const sportRows = sportCells
		? sportColumns
				.map((c) => ({ c, cell: cellOf(sportCells, c.key) }))
				.filter((x) => x.cell.total > 0)
				.sort((a, b) => b.cell.gold - a.cell.gold || b.cell.silver - a.cell.silver || b.cell.bronze - a.cell.bronze || b.cell.total - a.cell.total)
		: [];

	return (
		<div className="flex flex-col gap-[1.25rem]">
			<section aria-label={t("drawer.totals")} className="grid grid-cols-2 gap-[0.5rem] sm:grid-cols-5">
				<Figure label={t("drawer.rank")} value={row.total > 0 ? `${shared ? "=" : ""}${formatInteger(row.rank)}` : "—"} detail={t("drawer.ofNocs", { n: formatInteger(ofRows) })} />
				{MEDALS.map((m) => (
					<Figure
						key={m}
						label={tm(`medal.${m}`)}
						badge={<MedalBadge medal={m} className="size-[1.125rem] text-[0.5625rem]" />}
						value={formatInteger(m === "GOLD" ? row.gold : m === "SILVER" ? row.silver : row.bronze)}
					/>
				))}
				<Figure label={t("table.total")} value={formatInteger(row.total)} />
			</section>

			<section className="flex flex-col gap-[0.5rem]">
				<h3 className="font-bold text-[0.75rem] text-muted uppercase tracking-wide">{t("drawer.bySport")}</h3>
				{!sportCells ? (
					<Spinner className="size-[1.25rem] text-board" />
				) : sportRows.length === 0 ? (
					<p className="text-[0.875rem] text-muted">{t("drawer.none")}</p>
				) : (
					<StackedBars
						labelWidth="9rem"
						rows={sportRows.map(({ c, cell }) => ({
							key: c.key,
							title: c.name,
							label: c.name,
							segments: MEDALS.map((m) => ({ key: m, label: tm(`medal.${m}`), color: MEDAL_COLOR[m], value: m === "GOLD" ? cell.gold : m === "SILVER" ? cell.silver : cell.bronze })),
						}))}
					/>
				)}
			</section>

			{dayCells && dayColumns.length > 0 && (
				<section className="flex flex-col gap-[0.5rem]">
					<h3 className="font-bold text-[0.75rem] text-muted uppercase tracking-wide">{t("drawer.byDay")}</h3>
					<DayColumns
						days={dayColumns.map((c) => {
							const cell = cellOf(dayCells, c.key);
							return { day: c.key, awarded: { GOLD: cell.gold, SILVER: cell.silver, BRONZE: cell.bronze }, due: 0 };
						})}
						today={null}
					/>
				</section>
			)}

			<section className="flex flex-col gap-[0.5rem]">
				<h3 className="font-bold text-[0.75rem] text-muted uppercase tracking-wide">{t("drawer.medals")}</h3>
				{list.isPending ? (
					<Spinner className="size-[1.25rem] text-board" />
				) : list.isError ? (
					<p className="text-[0.875rem] text-conflict">{errorMessage(list.error)}</p>
				) : medals.length === 0 ? (
					<p className="text-[0.875rem] text-muted">{t("drawer.none")}</p>
				) : (
					<div className="flex flex-col gap-[0.75rem]">
						{bySport.map(([sport, items]) => (
							<div key={sport} className="flex flex-col">
								<h4 className="border-rule border-b pb-[0.25rem] font-bold text-[0.8125rem] text-ink">{sport}</h4>
								<ul className="flex flex-col">
									{items.map((m) => (
										<li key={m.event_medal_id} className="flex items-start gap-[0.625rem] border-rule border-b py-[0.5rem] last:border-b-0">
											<MedalBadge medal={m.medal} />
											<span className="flex min-w-0 flex-1 flex-col">
												<span className="font-bold text-[0.875rem] text-ink">{m.event.name}</span>
												<span className="truncate text-[0.8125rem] text-muted">{m.competitor.name}</span>
											</span>
											<time dateTime={m.awarded_at} title={formatDateTime(m.awarded_at)} className="shrink-0 font-mono text-[0.75rem] text-muted">
												{formatShort(m.awarded_at)}
											</time>
										</li>
									))}
								</ul>
							</div>
						))}
						{list.hasNextPage && (
							<Button size="sm" variant="outline" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
								{tc("actions.showMore")}
							</Button>
						)}
					</div>
				)}
			</section>
		</div>
	);
}

function Figure({ label, value, detail, badge }: { label: string; value: string; detail?: string; badge?: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-[0.125rem] rounded-btn border border-rule bg-paper px-[0.75rem] py-[0.5rem]">
			<span className="flex items-center gap-[0.375rem] font-bold text-[0.6875rem] text-muted uppercase tracking-wide">
				{badge}
				{label}
			</span>
			<span className="font-black text-[1.375rem] text-ink leading-tight">{value}</span>
			{detail && <span className="text-[0.75rem] text-muted">{detail}</span>}
		</div>
	);
}

/** The medals by sport, sports in the order first met (newest medal first), medals newest first inside. */
function groupBySport(items: readonly MedalListItem[]): Array<[string, MedalListItem[]]> {
	const out = new Map<string, MedalListItem[]>();
	for (const m of items) {
		const list = out.get(m.sport.name) ?? [];
		list.push(m);
		out.set(m.sport.name, list);
	}
	return [...out];
}
